/**
 * Le nom qu'un document se donne tout seul, quand l'avocat ne lui en a pas donné.
 *
 * ## Pourquoi
 *
 * Un brouillon vierge s'appelle « Document sans titre » — le défaut du schéma
 * partagé. Le renommage existe (double-clic sur le bandeau) mais ne se découvre
 * pas. Tant qu'une conversation ne portait qu'un document, cela ne coûtait
 * rien : il n'y avait rien à distinguer. Dès qu'elle peut en porter plusieurs,
 * une liste de six « Document sans titre » est **pire que pas de liste**.
 *
 * ## Ce que cette fonction décide, et ce qu'elle refuse
 *
 * Elle rend le premier texte du document, et **seulement s'il est arrêté** :
 * d'autres blocs existent après lui, donc l'avocat est passé à la suite. Une
 * ligne encore en cours de frappe ne devient jamais un nom.
 *
 * ⚠️ **Un paragraphe au style Titre n'y fait pas exception** (corrigé le
 * 2026-09-23). Il était accepté seul, au motif que son style prouvait que
 * l'avocat l'avait voulu tel. C'est vrai du style, pas du texte : le nommage
 * tourne à chaque frappe, donc un titre tapé lettre à lettre était lu dès son
 * deuxième caractère et figé en « Co ». Seul un titre écrit d'un coup (par
 * l'agent) était complet à la lecture.
 *
 * ⚠️ **C'est la garde essentielle.** Le nom dérivé est posé comme un titre
 * CHOISI — il est persisté, restauré, et ne se re-dérive plus ensuite (le faire
 * suivre le contenu demanderait de distinguer en base un titre dérivé d'un
 * titre choisi, donc une colonne). Nommer sur une frappe en cours figerait donc
 * « Courr » pour toujours, et un nom faux qui a l'air voulu est plus nuisible
 * que pas de nom du tout. Quand rien n'est arrêté, on rend `null` et on
 * réessaiera à la frappe suivante.
 */

/** Ce que la lecture du document fournit, réduit à ce qui décide ici. */
export interface BlocLuPourLeTitre {
  readonly fullText: string;
}

/**
 * Longueur maximale du nom dérivé.
 *
 * Le bandeau tronque déjà à la largeur réelle du panneau (~640 px, #826), et ce
 * nom sert aussi de nom de fichier au téléchargement : au-delà, on transporte
 * une phrase entière sans rien montrer de plus.
 */
const LONGUEUR_MAX = 60;

/** Un titre ne peut pas être une lettre isolée ni un reste de ponctuation. */
const LONGUEUR_MIN = 2;

function texteNormalise(brut: string): string {
  // Espaces typographiques, insécables et retours de ligne compris : le texte
  // vient d'un DOCX, pas d'un `<input>`.
  return brut.replace(/\s+/gu, ' ').trim();
}

function tronque(texte: string): string {
  if (texte.length <= LONGUEUR_MAX) return texte;
  const coupe = texte.slice(0, LONGUEUR_MAX);
  const dernierEspace = coupe.lastIndexOf(' ');
  // Couper au mot, sauf si le premier mot dépasse à lui seul la borne — auquel
  // cas on coupe net plutôt que de rendre une chaîne vide.
  const retenu = dernierEspace > LONGUEUR_MIN ? coupe.slice(0, dernierEspace) : coupe;
  return retenu.trimEnd();
}

/**
 * @param blocs Les premiers blocs du document, dans l'ordre.
 * @param dAutresBlocsSuivent Le document en porte-t-il au-delà de `blocs` ?
 *   Seulement consulté quand le texte retenu est le DERNIER bloc lu : sans
 *   cela, une première ligne terminée suivie d'une seule autre ligne serait
 *   jugée « en cours » parce que la fenêtre de lecture s'arrête là.
 * @returns le nom à poser, ou `null` s'il n'y a rien d'arrêté à nommer.
 */
export function titreDeriveDuDocument(
  blocs: readonly BlocLuPourLeTitre[],
  dAutresBlocsSuivent = false,
): string | null {
  const index = blocs.findIndex((bloc) => texteNormalise(bloc.fullText).length >= LONGUEUR_MIN);
  if (index === -1) return null;
  const texte = texteNormalise(blocs[index]!.fullText);

  // La preuve que le texte est fini : quelque chose derrière lui. Vaut aussi
  // pour un titre, tapé lettre à lettre comme le reste (voir l'en-tête).
  const ligneTerminee = index < blocs.length - 1 || dAutresBlocsSuivent;
  if (!ligneTerminee) return null;

  return tronque(texte);
}
